import {
  Server,
  Cpu,
  HardDrive,
  Activity,
  Zap,
  DollarSign,
  Settings,
  ChevronDown,
  ChevronUp,
  Check,
  Shield,
  Palette,
  FlaskConical,
  Box,
  Monitor,
  Container,
  Layers,
} from 'lucide-react';
import { Card, CardTitle, CardDescription, Button } from '../../ui';
import { useConfigurator } from '../../../hooks/useConfigurator';
import { SCENARIO_PRESETS, FORM_FACTORS } from '../../../data/scenarios';
import { ScenarioType, FormFactor } from '../../../types/configurator';
import { clsx } from 'clsx';
import { useState } from 'react';

const SCENARIO_ICONS: Record<ScenarioType, typeof Server> = {
  minimal: Server,
  'lab-bench': Activity,
  research: Cpu,
  industrial: HardDrive,
  'gdpr-llm': Shield,
  'ai-rendering': Palette,
  'lab-testing': FlaskConical,
  custom: Settings,
};

const FORM_FACTOR_ICONS: Record<FormFactor, typeof Box> = {
  'rack-18': Box,
  'rack-42': Layers,
  desktop: Monitor,
  container: Container,
  embedded: Cpu,
};

export function ScenarioStep() {
  const { config, setScenario, setFormFactor } = useConfigurator();
  const [showComparison, setShowComparison] = useState(false);

  // Filter scenarios that exclude 'custom' for comparison table
  const comparableScenarios = SCENARIO_PRESETS.filter((s) => s.id !== 'custom');

  return (
    <div className="space-y-8">
      {/* Form Factor Selection */}
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Delivery Method</h2>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            Choose how your datacenter will be deployed. This filters available hardware options.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {FORM_FACTORS.map((ff) => {
            const Icon = FORM_FACTOR_ICONS[ff.id];
            const isSelected = config.formFactor === ff.id;

            return (
              <button
                key={ff.id}
                onClick={() => setFormFactor(ff.id)}
                className={clsx(
                  'relative flex flex-col items-center gap-2 rounded-lg border-2 p-4 text-center transition-all',
                  isSelected
                    ? 'border-hermit-500 bg-hermit-50 dark:bg-hermit-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600',
                  ff.recommended && !isSelected && 'ring-1 ring-hermit-200 dark:ring-hermit-800'
                )}
              >
                {ff.recommended && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 rounded-full bg-hermit-500 px-2 py-0.5 text-xs font-medium text-white">
                    Recommended
                  </span>
                )}
                <Icon
                  className={clsx(
                    'h-8 w-8',
                    isSelected
                      ? 'text-hermit-600 dark:text-hermit-400'
                      : 'text-gray-400 dark:text-gray-500'
                  )}
                />
                <div>
                  <p
                    className={clsx(
                      'font-medium',
                      isSelected
                        ? 'text-hermit-700 dark:text-hermit-300'
                        : 'text-gray-700 dark:text-gray-300'
                    )}
                  >
                    {ff.name}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                    {ff.description}
                  </p>
                </div>
                {isSelected && (
                  <div className="absolute right-2 top-2">
                    <Check className="h-4 w-4 text-hermit-600 dark:text-hermit-400" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Scenario Selection */}
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Choose Your Scenario</h2>
            <p className="mt-1 text-gray-500 dark:text-gray-400">
              Select a deployment configuration that matches your use case. You can customize it later.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowComparison(!showComparison)}
            className="gap-1.5"
          >
            {showComparison ? (
              <>
                <ChevronUp className="h-4 w-4" />
                Hide Comparison
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" />
                Compare All
              </>
            )}
          </Button>
        </div>

        {/* Comparison Table */}
        {showComparison && (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Feature</th>
                  {comparableScenarios.map((s) => (
                    <th key={s.id} className="px-4 py-3 text-center font-medium">
                      <button
                        onClick={() => setScenario(s.id)}
                        className={clsx(
                          'rounded-lg px-2 py-1 transition-colors',
                          config.scenario === s.id
                            ? 'bg-hermit-100 dark:bg-hermit-900 text-hermit-700 dark:text-hermit-300'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                        )}
                      >
                        {s.name}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                <tr>
                  <td className="px-4 py-2 text-gray-500 dark:text-gray-400">Nodes</td>
                  {comparableScenarios.map((s) => (
                    <td key={s.id} className="px-4 py-2 text-center font-medium text-gray-900 dark:text-white">
                      {s.nodeCount}
                    </td>
                  ))}
                </tr>
                <tr className="bg-gray-50/50 dark:bg-gray-900/50">
                  <td className="px-4 py-2 text-gray-500 dark:text-gray-400">Power</td>
                  {comparableScenarios.map((s) => (
                    <td key={s.id} className="px-4 py-2 text-center font-medium text-gray-900 dark:text-white">
                      {s.estimatedPower}W
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-4 py-2 text-gray-500 dark:text-gray-400">Est. Cost</td>
                  {comparableScenarios.map((s) => (
                    <td key={s.id} className="px-4 py-2 text-center font-medium text-gray-900 dark:text-white">
                      ${s.estimatedCost.toLocaleString()}
                    </td>
                  ))}
                </tr>
                <tr className="bg-gray-50/50 dark:bg-gray-900/50">
                  <td className="px-4 py-2 text-gray-500 dark:text-gray-400">GPU Nodes</td>
                  {comparableScenarios.map((s) => {
                    const gpuCount = s.defaultNodes.filter(
                      (n) => n.specs.gpu
                    ).reduce((a, n) => a + n.count, 0);
                    return (
                      <td key={s.id} className="px-4 py-2 text-center font-medium text-gray-900 dark:text-white">
                        {gpuCount > 0 ? gpuCount : <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td className="px-4 py-2 text-gray-500 dark:text-gray-400">Storage</td>
                  {comparableScenarios.map((s) => {
                    const count = s.defaultNodes.filter((n) => n.type === 'storage').reduce((a, n) => a + n.count, 0);
                    return (
                      <td key={s.id} className="px-4 py-2 text-center font-medium text-gray-900 dark:text-white">
                        {count > 0 ? count : <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                    );
                  })}
                </tr>
                <tr className="bg-gray-50/50 dark:bg-gray-900/50">
                  <td className="px-4 py-2 text-gray-500 dark:text-gray-400">DAQ</td>
                  {comparableScenarios.map((s) => {
                    const count = s.defaultNodes.filter((n) => n.type === 'daq').reduce((a, n) => a + n.count, 0);
                    return (
                      <td key={s.id} className="px-4 py-2 text-center font-medium text-gray-900 dark:text-white">
                        {count > 0 ? count : <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {SCENARIO_PRESETS.map((scenario) => {
            const Icon = SCENARIO_ICONS[scenario.id];
            const isSelected = config.scenario === scenario.id;
            const hasGpu = scenario.defaultNodes.some((n) => n.specs.gpu);

            return (
              <Card
                key={scenario.id}
                selected={isSelected}
                onClick={() => setScenario(scenario.id)}
                className="relative"
              >
                <div className="flex items-start gap-4">
                  <div
                    className={clsx(
                      'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg',
                      scenario.id === 'custom'
                        ? isSelected
                          ? 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400'
                          : 'bg-purple-50 dark:bg-purple-900/30 text-purple-400 dark:text-purple-500'
                        : isSelected
                          ? 'bg-hermit-100 dark:bg-hermit-900 text-hermit-600 dark:text-hermit-400'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                    )}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle>{scenario.name}</CardTitle>
                    <CardDescription className="mt-1">
                      {scenario.description}
                    </CardDescription>

                    <div className="mt-4 flex flex-wrap gap-3 text-sm">
                      {scenario.id === 'custom' ? (
                        <div className="flex items-center gap-1 text-purple-600 dark:text-purple-400 font-medium">
                          <Settings className="h-4 w-4" />
                          <span>Build your own</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                            <Server className="h-4 w-4" />
                            <span>{scenario.nodeCount} nodes</span>
                          </div>
                          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                            <Zap className="h-4 w-4" />
                            <span>{scenario.estimatedPower}W</span>
                          </div>
                          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                            <DollarSign className="h-4 w-4" />
                            <span>~${scenario.estimatedCost.toLocaleString()}</span>
                          </div>
                          {hasGpu && (
                            <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                              <Cpu className="h-4 w-4" />
                              <span>GPU</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Use cases:</p>
                      <ul className="mt-1 flex flex-wrap gap-1">
                        {scenario.useCases.slice(0, 3).map((useCase) => (
                          <li
                            key={useCase}
                            className="rounded bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-400"
                          >
                            {useCase}
                          </li>
                        ))}
                        {scenario.useCases.length > 3 && (
                          <li className="rounded bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs text-gray-500 dark:text-gray-500">
                            +{scenario.useCases.length - 3} more
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>

                {isSelected && (
                  <div className="absolute right-3 top-3">
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-hermit-600 text-white">
                      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
